import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, X, BookOpen, ChevronRight, Play, Video } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

// Resolve /api/... image paths against the backend origin so they load
// correctly when the page is served from a CDN/static origin too.
const imgUrl = (p) => (p && p.startsWith("/api/") ? `${BACKEND_ORIGIN}${p}` : p);

export default function Principles() {
  const { t } = useLanguage();
  const [principles, setPrinciples] = useState([]);
  const [openRule, setOpenRule] = useState(null); // index of open rule
  const [openMode, setOpenMode] = useState("read"); // "read" or "watch"

  useEffect(() => {
    axios.get(`${API}/principles`).then((r) => setPrinciples(r.data)).catch(() => {});
  }, []);

  const openInMode = (i, mode) => { setOpenRule(i); setOpenMode(mode); };

  return (
    <div className="fade-in max-w-6xl mx-auto px-6 lg:px-12 py-16">
      <div className="mb-12 max-w-2xl">
        <div className="overline mb-3">12 Rules · Page-turn experience</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A]">
          {t.principles_title}
        </h1>
        <p className="mt-4 text-[#475569] text-lg leading-relaxed">{t.principles_sub}</p>
      </div>

      {/* Grid of rule covers — click to open the book */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="principles-grid">
        {principles.map((p, i) => (
          <div
            key={p.id || i}
            data-testid={`principle-card-${i}`}
            className="text-left group card overflow-hidden hover:-translate-y-1 transition-all duration-300 relative cursor-pointer"
            onClick={() => openInMode(i, "watch")}
          >
            <div className="aspect-[16/9] bg-[#EEF2FF] overflow-hidden relative">
              <img
                src={imgUrl(p.hero_image)}
                alt={p.title}
                loading="lazy"
                onError={(e) => { e.currentTarget.style.opacity = "0.1"; }}
                className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute top-3 left-3 text-[10px] tracking-[0.25em] uppercase font-bold text-white/90">
                Rule {String(p.rule_number).padStart(2, "0")}
              </div>
              {/* Centred Play icon to signal video-first */}
              <div className="absolute inset-0 flex items-center justify-center opacity-90 group-hover:opacity-100 transition-opacity">
                <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 text-[#002FA7] fill-current ml-1" strokeWidth={0} />
                </div>
              </div>
              <button
                data-testid={`read-rule-${i}`}
                onClick={(e) => { e.stopPropagation(); openInMode(i, "read"); }}
                className="absolute top-3 right-3 inline-flex items-center gap-1 bg-white/95 hover:bg-white text-[#002FA7] text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow"
                aria-label="Read the rule as a book"
              >
                <BookOpen className="w-3 h-3" strokeWidth={2} /> Read
              </button>
            </div>
            <div className="p-5">
              <h3 className="font-display text-lg font-bold tracking-tight text-[#0F172A]">{p.title}</h3>
              <p className="mt-2 text-sm text-[#475569] line-clamp-2 leading-relaxed">{p.summary}</p>
              <div className="mt-4 flex items-center gap-1 text-xs font-medium text-[#002FA7]">
                <Play className="w-3.5 h-3.5 fill-current" strokeWidth={0} />
                Watch the rule
                <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {openRule !== null && principles[openRule] && (
        <RuleBook
          principle={principles[openRule]}
          mode={openMode}
          onModeChange={setOpenMode}
          onClose={() => setOpenRule(null)}
          onPrev={openRule > 0 ? () => setOpenRule(openRule - 1) : null}
          onNext={openRule < principles.length - 1 ? () => setOpenRule(openRule + 1) : null}
        />
      )}
    </div>
  );
}

// ============================================================================
// RuleBook — full-screen modal with 3D book-flip pages
// ============================================================================
function RuleBook({ principle, mode, onModeChange, onClose, onPrev, onNext }) {
  // Pages: 0 = hero/cover, 1..6 = subpoints
  const pages = useMemo(() => {
    const out = [{
      kind: "cover",
      title: principle.title,
      summary: principle.summary,
      detail: principle.detail,
      image: principle.hero_image,
      alt: principle.title,
    }];
    (principle.subpoints || []).forEach((sp, i) => {
      out.push({
        kind: "subpoint",
        index: i + 1,
        text: sp.text,
        image: sp.image,
        alt: sp.alt || sp.text,
      });
    });
    return out;
  }, [principle]);

  const [pageIdx, setPageIdx] = useState(0);
  const [direction, setDirection] = useState("next"); // 'next' or 'prev'
  const [flipping, setFlipping] = useState(false);

  // Reset to first page when the rule changes
  useEffect(() => { setPageIdx(0); setDirection("next"); }, [principle]);

  const goTo = (newIdx, dir) => {
    if (flipping) return;
    setDirection(dir);
    setFlipping(true);
    setTimeout(() => {
      setPageIdx(newIdx);
      setFlipping(false);
    }, 380); // matches CSS transition
  };

  const next = () => {
    if (pageIdx < pages.length - 1) goTo(pageIdx + 1, "next");
    else if (onNext) onNext();
  };
  const prev = () => {
    if (pageIdx > 0) goTo(pageIdx - 1, "prev");
    else if (onPrev) onPrev();
  };

  // Keyboard nav — only active in read mode (book pagination)
  useEffect(() => {
    if (mode !== "read") return;
    const handler = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [pageIdx, flipping, onPrev, onNext, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes the modal in any mode
  useEffect(() => {
    const escOnly = (e) => { if (e.key === "Escape") onClose(); };
    if (mode === "watch") window.addEventListener("keydown", escOnly);
    return () => window.removeEventListener("keydown", escOnly);
  }, [mode, onClose]);

  const current = pages[pageIdx];

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0F172A]/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 lg:py-10 lg:px-8 fade-in"
      data-testid="rule-book"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between text-white">
        <div className="flex items-center gap-3">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold opacity-70">
            Rule {String(principle.rule_number).padStart(2, "0")}
          </div>
          <div className="hidden sm:block text-sm font-medium opacity-90 truncate max-w-md">{principle.title}</div>
        </div>
        <div className="flex items-center gap-4">
          {/* Read/Watch toggle */}
          <div className="hidden sm:inline-flex items-center bg-white/10 rounded-full p-0.5" data-testid="mode-toggle">
            <button
              onClick={() => onModeChange("read")}
              data-testid="mode-read"
              className={`text-[11px] tracking-wider font-bold px-3 py-1 rounded-full transition-all ${
                mode === "read" ? "bg-white text-[#0F172A]" : "text-white/70 hover:text-white"
              }`}
            >
              <BookOpen className="w-3 h-3 inline mr-1" strokeWidth={2} /> READ
            </button>
            <button
              onClick={() => onModeChange("watch")}
              data-testid="mode-watch"
              className={`text-[11px] tracking-wider font-bold px-3 py-1 rounded-full transition-all ${
                mode === "watch" ? "bg-white text-[#0F172A]" : "text-white/70 hover:text-white"
              }`}
            >
              <Video className="w-3 h-3 inline mr-1" strokeWidth={2} /> WATCH
            </button>
          </div>
          <div className="text-xs opacity-70 tracking-wider" data-testid="page-indicator">
            {mode === "read" ? `${pageIdx + 1} / ${pages.length}` : "VIDEO"}
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-book">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Progress segments (only in read mode) */}
      {mode === "read" && (
        <div className="absolute top-12 sm:top-14 left-4 right-4 sm:left-6 sm:right-6 flex gap-1">
          {pages.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 bg-white/15 overflow-hidden rounded-full">
              <div
                className="h-full bg-white transition-all duration-300"
                style={{ width: i < pageIdx ? "100%" : i === pageIdx ? "100%" : "0%" }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Book stage OR video player */}
      {mode === "watch" ? (
        <VideoStage principle={principle} onEnded={onNext || onClose} hasNext={!!onNext} />
      ) : (
        <div
          className="relative w-auto h-[78vh] max-h-[780px] min-h-[420px] aspect-[4/5] sm:aspect-[5/6] lg:aspect-[4/5] perspective"
          data-testid="book-stage"
        >
          <div
            key={pageIdx}
            className={`book-page ${flipping ? `flip-${direction}` : ""}`}
          >
            {current.kind === "cover" ? (
              <CoverPage page={current} ruleNumber={principle.rule_number} />
            ) : (
              <SubPage page={current} />
            )}
          </div>
        </div>
      )}

      {/* Side nav buttons (desktop) — read mode only */}
      {mode === "read" && (
        <>
          <button
            onClick={prev}
            disabled={pageIdx === 0 && !onPrev}
            className={`hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 rounded-full border border-white/20 text-white transition-all ${
              pageIdx === 0 && !onPrev ? "opacity-30 cursor-not-allowed" : "hover:bg-white/10"
            }`}
            data-testid="book-prev"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button
            onClick={next}
            className={`hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 rounded-full border border-white/20 text-white transition-all hover:bg-white/10`}
            data-testid="book-next"
          >
            <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
          </button>

          {/* Mobile bottom nav */}
          <div className="md:hidden absolute bottom-20 left-4 right-4 flex items-center justify-between gap-4 z-[9999]">
            <button onClick={prev} className="btn-secondary !text-white !border-white/30 !bg-white/5 flex-1 justify-center" data-testid="book-prev-mobile">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Prev
            </button>
            <button onClick={next} className="btn-primary flex-1 justify-center" data-testid="book-next-mobile">
              Next <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          {/* Tap-zones for full-screen advance on mobile */}
          <button
            className="md:hidden absolute left-0 top-20 bottom-20 w-1/3"
            aria-label="Previous page"
            onClick={prev}
          />
          <button
            className="md:hidden absolute right-0 top-20 bottom-20 w-1/3"
            aria-label="Next page"
            onClick={next}
          />
        </>
      )}
    </div>
  );
}

function VideoStage({ principle, onEnded, hasNext }) {
  const src = imgUrl(principle.video);
  return (
    <div
      className="relative w-auto h-[78vh] max-h-[780px] min-h-[360px] aspect-video lg:aspect-video bg-black rounded-md overflow-hidden shadow-2xl"
      data-testid="video-stage"
    >
      <video
        key={src}
        src={src}
        controls
        autoPlay
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-contain bg-black"
        data-testid="rule-video"
        onError={(e) => { e.currentTarget.poster = imgUrl(principle.hero_image); }}
        onEnded={onEnded}
        poster={imgUrl(principle.hero_image)}
      >
        Your browser doesn't support embedded video.
      </video>
      {hasNext && (
        <div className="absolute bottom-3 right-3 text-[11px] tracking-wider font-medium text-white/80 bg-black/45 px-2.5 py-1 rounded-full pointer-events-none">
          Next rule plays automatically →
        </div>
      )}
    </div>
  );
}

function CoverPage({ page, ruleNumber }) {
  return (
    <div className="book-card flex flex-col">
      <div className="relative flex-1 overflow-hidden bg-[#0F172A]">
        <img
          src={imgUrl(page.image)}
          alt={page.alt}
          loading="lazy"
          onError={(e) => { e.currentTarget.style.opacity = "0"; }}
          className="absolute inset-0 w-full h-full object-cover ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 text-white">
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold opacity-80 mb-3">
            Rule {String(ruleNumber).padStart(2, "0")}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]">
            {page.title}
          </h2>
          <p className="mt-4 text-sm sm:text-base text-white/85 max-w-xl leading-relaxed">
            {page.summary}
          </p>
        </div>
      </div>
      <div className="bg-white px-6 sm:px-10 py-5 border-t border-[#E2E8F0]">
        <p className="text-sm text-[#475569] leading-relaxed italic">{page.detail}</p>
      </div>
    </div>
  );
}

function SubPage({ page }) {
  return (
    <div className="book-card flex flex-col">
      <div className="relative flex-1 overflow-hidden bg-[#F8FAFC]">
        <img
          src={imgUrl(page.image)}
          alt={page.alt}
          loading="lazy"
          onError={(e) => { e.currentTarget.style.opacity = "0"; }}
          className="absolute inset-0 w-full h-full object-cover ken-burns"
        />
      </div>
      <div className="bg-white px-6 sm:px-10 py-6 border-t border-[#E2E8F0]">
        <div className="overline text-[#002FA7] mb-2">Point {page.index} of 6</div>
        <p className="font-display text-lg sm:text-xl font-semibold tracking-tight text-[#0F172A] leading-snug">
          {page.text}
        </p>
      </div>
    </div>
  );
}
